import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Send, X as XIcon } from "lucide-react";

const STATUS_LABELS = {
  pending: "Pending payment",
  paid: "Paid",
  ordered: "Ordered",
  ready_for_pickup: "Ready for Pickup",
  ready_for_delivery: "Ready for Delivery",
  completed: "Completed",
};

/**
 * Modal shown when admin changes an order's status. Pre-renders the saved
 * SMS+email template against this order, lets admin tweak the text per-order,
 * pick channels (sms / email / both / none), and either:
 *   • Send + advance status, or
 *   • Skip notification (just advance status), or
 *   • Cancel.
 *
 * Defers all state hydration to /preview-status-notification so admin always
 * sees the latest template content without a full page reload.
 */
export default function StatusChangeModal({ order, newStatus, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [channel, setChannel] = useState("both");
  const [smsBody, setSmsBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!order || !newStatus) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/admin/orders/${order.id}/preview-status-notification`,
          { params: { new_status: newStatus } },
        );
        if (cancelled) return;
        setPreview(r.data);
        setSmsBody(r.data.sms_body || "");
        setEmailSubject(r.data.email_subject || "");
        setEmailBody(r.data.email_body || "");
        // Default channel respects what's enabled on the template.
        const sms = r.data.sms_enabled;
        const email = r.data.email_enabled;
        setChannel(sms && email ? "both" : sms ? "sms" : email ? "email" : "none");
      } catch {
        toast.error("Couldn't load notification template");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [order, newStatus, onClose]);

  if (!order || !newStatus) return null;

  const send = async ({ skipStatusUpdate = false } = {}) => {
    setSending(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/send-status-notification`, {
        new_status: newStatus,
        channel,
        sms_body: smsBody,
        email_subject: emailSubject,
        email_body: emailBody,
        skip_status_update: skipStatusUpdate,
      });
      const sentSms = r.data?.results?.sms;
      const sentEmail = r.data?.results?.email;
      const channels = [
        sentSms ? (sentSms.sent ? "SMS sent" : sentSms.stubbed ? "SMS logged" : "SMS failed") : null,
        sentEmail ? (sentEmail.sent ? "Email sent" : sentEmail.stubbed ? "Email logged" : "Email failed") : null,
      ].filter(Boolean);
      toast.success(channels.length ? channels.join(" · ") : "Done");
      onApplied?.(r.data.order);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  // Status-change with NO notification.
  const skipNotification = async () => {
    setSending(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status: newStatus });
      toast.success(`Status: ${newStatus} · no notification sent`);
      onApplied?.({ ...order, status: newStatus });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 overflow-y-auto py-8"
      data-testid="status-change-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[var(--brand-border)] px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider muted-text font-bold">Status change</p>
            <h2 className="font-bold text-[var(--brand-text)]">
              {order.order_number} → {STATUS_LABELS[newStatus] || newStatus}
            </h2>
          </div>
          <button
            data-testid="status-change-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--brand-green-light)]"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 muted-text text-sm">Loading template…</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="rounded-xl bg-[var(--brand-green-light)] border border-[var(--brand-border)] px-3 py-2 text-xs text-[var(--brand-text)]">
              Sending to <strong>{preview?.customer_name}</strong>
              {preview?.customer_phone && <> · {preview.customer_phone}</>}
              {preview?.customer_email && <> · {preview.customer_email}</>}
            </div>

            <div>
              <p className="text-xs font-semibold muted-text mb-1.5">Channel</p>
              <div className="flex flex-wrap gap-2">
                {["both", "sms", "email", "none"].map((opt) => {
                  const disabled =
                    (opt === "sms" && !preview?.customer_phone) ||
                    (opt === "email" && !preview?.customer_email) ||
                    (opt === "both" && !(preview?.customer_phone && preview?.customer_email));
                  return (
                    <button
                      key={opt}
                      data-testid={`status-change-channel-${opt}`}
                      disabled={disabled}
                      onClick={() => setChannel(opt)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition ${
                        channel === opt
                          ? "brand-bg text-white border-transparent"
                          : "border-[var(--brand-border)] hover:border-[var(--brand-green)]"
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                    >
                      {opt === "both" ? "SMS + Email" : opt === "none" ? "No notification" : opt.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            {(channel === "sms" || channel === "both") && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold muted-text">SMS</label>
                  <span className="text-[10px] muted-text">{smsBody.length} chars</span>
                </div>
                <textarea
                  data-testid="status-change-sms-body"
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm"
                />
              </div>
            )}

            {(channel === "email" || channel === "both") && (
              <div>
                <label className="text-xs font-semibold muted-text">Email subject</label>
                <input
                  data-testid="status-change-email-subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1 mb-2"
                />
                <label className="text-xs font-semibold muted-text">Email body</label>
                <textarea
                  data-testid="status-change-email-body"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={8}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1 font-mono"
                />
              </div>
            )}

            <div className="pt-2 border-t border-[var(--brand-border)] flex flex-col gap-2">
              {channel === "none" ? (
                <button
                  data-testid="status-change-update-only"
                  onClick={skipNotification}
                  disabled={sending}
                  className="w-full py-2.5 rounded-full font-semibold text-sm brand-bg text-white disabled:opacity-50"
                >
                  Update status (no notification)
                </button>
              ) : (
                <button
                  data-testid="status-change-confirm-send"
                  onClick={() => send({ skipStatusUpdate: false })}
                  disabled={sending || (channel !== "none" && !smsBody && !emailBody)}
                  className="w-full py-2.5 rounded-full font-semibold text-sm brand-bg text-white disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {sending ? "Sending…" : `Confirm & send (${channel === "both" ? "SMS + Email" : channel.toUpperCase()})`}
                </button>
              )}
              <div className="flex gap-2">
                {channel !== "none" && (
                  <button
                    data-testid="status-change-skip-notif"
                    onClick={skipNotification}
                    disabled={sending}
                    className="flex-1 py-2 rounded-full font-semibold text-sm border-2 border-[var(--brand-border)] disabled:opacity-50"
                  >
                    Skip notification
                  </button>
                )}
                <button
                  data-testid="status-change-cancel"
                  onClick={onClose}
                  disabled={sending}
                  className="flex-1 py-2 rounded-full font-semibold text-sm border-2 border-[var(--brand-border)] disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
